import { GroupCategoriesMap } from '@/store/chats/constants'
import type {
  Chat,
  ChatList,
  GroupCategories,
  IGroupChatMember
} from '@/store/chats/IChats'
import memoize from 'memoize-one'
import type { ILastMessages, IMessage } from '@/store/messages/interfaces'
import {
  isAlogixChat,
  isNewsChat
} from '@/components/Chat/ChatItem/predicates'
import type { AlogixChat } from '@/store/alogixChat/alogixChat'
import { CustomChatNames } from '@/constants/CustomChats'
import { ChatsDataWithDivider, INewsChat } from './IChats'

const MU_CHAT_REGEX = new RegExp(`.+@conference.${import.meta.env.VITE_EJ_HOST}$`)
const PRIVATE_CHAT_REGEX = new RegExp(`.+@${import.meta.env.VITE_EJ_HOST}$`)

export const isMUChat = (chat?: Chat | string): boolean => {
  if (!chat) {
    return false
  }
  if (typeof chat === 'string') {
    return MU_CHAT_REGEX.test(chat)
  }
  if (chat.type != null) {
    return chat.type === 'groupchat'
  }
  return MU_CHAT_REGEX.test(chat.$jid)
}

export const isPrivateChat = (chat?: Chat | string): boolean => {
  if (!chat) {
    return false
  }
  if (typeof chat === 'string') {
    return PRIVATE_CHAT_REGEX.test(chat)
  }
  if (chat.type != null) {
    return chat.type === 'chat'
  }
  return PRIVATE_CHAT_REGEX.test(chat.$jid)
}

export const isRoomMessage = (message: IMessage) => {
  const reg = new RegExp(`@conference.${import.meta.env.VITE_EJ_HOST}$`)
  return reg.test(message.from) || reg.test(message.to)
}

export const getChatJid = (message: IMessage, userJid: string) => {
  if (isRoomMessage(message)) {
    return message.to
  }
  return message.from === userJid ? message.to : message.from
}

export const createChatsData =
  memoize((
    {
      chats,
      ...props
    }: ChatsDataWithDivider) => ({
    chats,
    ...props,
    length: chats.length
  }))

export const getChatId = (jid: string) => {
  const index = jid.indexOf('@')
  return index > 0 ? jid.slice(0, index) : jid
}

const filterChat = (filter: string) => (chat: Chat | AlogixChat): boolean => {
  const id = isAlogixChat(chat) ? chat.id : chat.$jid
  return !!(getChatId(id).toLowerCase().includes(filter.toLowerCase().trim())
    || chat.name?.toLowerCase().includes(filter.toLowerCase().trim())
    || chat.nick?.toLowerCase().includes(filter.toLowerCase().trim()))
}

export const filterChats = (chats: Chat[], filter = '') => {
  const filterCb = filterChat(filter)
  return chats.filter(filterCb)
}

export const filterChatsWithNews = (chats: (Chat | INewsChat | AlogixChat)[], filter = '') => {
  const filterCb = filterChat(filter)
  return chats.filter(chat => isNewsChat(chat)
    ? chat.name.toLowerCase().includes(filter.toLowerCase().trim())
    : filterCb(chat))
}

export const getApprovedChats = (chats: Chat[] | undefined) => {
  return chats?.filter(chat => filterAcceptedChats(chat) || filterFavoriteChats(chat)) || []
}

export const getAcceptedContacts = (contacts: Chat[]) => {
  return contacts.filter(contact => contact.$subscription === 'both')
}

export const filterFavoriteChats = (chat: Chat | AlogixChat) =>
  !!chat.groups?.includes(GroupCategoriesMap.favorite)

export const filterAcceptedChats = (chat: Chat | AlogixChat): boolean =>
  !!(isAlogixChat(chat) ||
    (chat.type === 'groupchat' && chat.$subscription !== 'none') ||
    ((chat.groups?.length === 0 || chat.groups?.includes(GroupCategoriesMap.accepted)) && chat.$subscription === 'both'))

export const filterChatsWithoutFavorites = (chat: Chat | INewsChat | AlogixChat) => {
  return isNewsChat(chat) || isAlogixChat(chat) || !filterFavoriteChats(chat)
}

export const filterAcceptedWithoutFavorites = (chat: Chat | INewsChat | AlogixChat) => {
  return isNewsChat(chat) || (filterAcceptedChats(chat) && !filterFavoriteChats(chat))
}

export const filterAcceptedWithFavorites = (chat: Chat) => {
  return filterAcceptedChats(chat) || filterFavoriteChats(chat)
}

export const filterConsultantsChannel = (chat: Chat) =>
  !!(chat.type === 'groupchat' && chat.groups?.includes(GroupCategoriesMap.consultant))

export const filterCustomersChannel = (chat: Chat) =>
  !!(chat.type === 'groupchat' && chat.groups?.includes(GroupCategoriesMap.customer))

export const filterSponsorChannel = (chat: Chat) =>
  !!(chat.type === 'groupchat' && chat.groups?.includes(GroupCategoriesMap.sponsor)
  )

export const filterTeamContactsWithoutSponsor = (chat: Chat) => {
  const groups = new Set(chat.groups || [])
  return chat.type === 'chat' && (
    groups.has(GroupCategoriesMap.customer)
    || groups.has(GroupCategoriesMap.consultant)
  )
}

export const filterTeamChats = (chat: Chat) => {
  const groups = new Set(chat.groups || [])
  return groups.has(GroupCategoriesMap.customer)
    || groups.has(GroupCategoriesMap.sponsor)
    || groups.has(GroupCategoriesMap.consultant)
}

export const filterSponsorContacts = (chat: Chat) => {
  return chat.type === 'chat' && chat.groups?.includes(GroupCategoriesMap.sponsor)
}

export const filterConsultantsContacts = (chat: Chat) => {
  return chat.type === 'chat' && chat.groups?.includes(GroupCategoriesMap.consultant)
}

export const filterCustomersContacts = (chat: Chat) => {
  return chat.type === 'chat' && chat.groups?.includes(GroupCategoriesMap.customer)
}

export const contactListToArray = (list: ChatList) => {
  return Object.keys(list).map(key => list[key]!)
}

export const getName = (chat: Chat | null | undefined, incoming: boolean) => {
  if (!incoming) {
    return ''
  }
  if (chat?.nick) {
    return chat?.nick
  }
  if (chat) {
    return chat.vcard?.nickname
  }
  return null
}

export const checkOwner = (userJid: string, members: IGroupChatMember[]) => {
  const user = members.find(member => member.jid === userJid)
  return user?.affiliation === 'owner'
}

export const checkAdmin = (userJid: string, members: IGroupChatMember[]) => {
  const user = members.find(member => member.jid === userJid)
  return user?.affiliation === 'admin'
}

const getTimeStamp = (
  chat: string | Chat | INewsChat | AlogixChat, timestamps: Record<string, number | undefined>
) => {
  const newsChat = typeof chat !== 'string' && isNewsChat(chat)
  const alogixChat1 = chat === CustomChatNames.ALOGIX || (typeof chat !== 'string' && isAlogixChat(chat))
  let jid1: string
  if (typeof chat === 'string') {
    jid1 = chat
  } else if (newsChat) {
    jid1 = ''
  } else if (alogixChat1) {
    jid1 = chat.id
  } else {
    jid1 = chat.$jid
  }
  let time: number
  if (newsChat) {
    time = chat.news[chat.news.length - 1]?.timestamp || 0
  } else {
    time = timestamps[jid1] || 0
  }
  return time
}

export const sortChatsByTimestamp = (timestamps: Record<string, number | undefined>) =>
  (
    chat1: string | Chat | INewsChat | AlogixChat,
    chat2: string | Chat | INewsChat | AlogixChat
  ) => {
    return getTimeStamp(chat2, timestamps) - getTimeStamp(chat1, timestamps)
}

export const sortChatsByTimestampWithPriority = (timestamps: Record<string, number | undefined>) =>
  (
    chat1: string | Chat | INewsChat | AlogixChat,
    chat2: string | Chat | INewsChat | AlogixChat
  ) => {
    if (typeof chat1 !== 'string' && isNewsChat(chat1)) {
      return -1
    }
    if (typeof chat2 !== 'string' && isNewsChat(chat2)) {
      return 1
    }
    if (chat1 === CustomChatNames.ALOGIX || (typeof chat1 !== 'string' && isAlogixChat(chat1))) {
      return -1
    }
    if (chat2 === CustomChatNames.ALOGIX || (typeof chat2 !== 'string' && isAlogixChat(chat2))) {
      return 1
    }
    return getTimeStamp(chat2, timestamps) - getTimeStamp(chat1, timestamps)
  }

export const sortChatsCache = (messages: ILastMessages) => (
  chat1: string | Chat | INewsChat | AlogixChat,
  chat2: string | Chat | INewsChat | AlogixChat
) => {
  if (chat1 === CustomChatNames.ALOGIX ||
    (typeof chat1 !== 'string' && isAlogixChat(chat1))) {
    return -1
  }
  if (chat2 === CustomChatNames.ALOGIX ||
    (typeof chat2 !== 'string' && isAlogixChat(chat2))) {
    return 1
  }
  const isNewsChat1 = typeof chat1 !== 'string' && isNewsChat(chat1)
  const isNewsChat2 = typeof chat2 !== 'string' && isNewsChat(chat2)
  const jid1 = typeof chat1 === 'string'
    ? chat1
    : isNewsChat1 ? '' : chat1.$jid
  const jid2 = typeof chat2 === 'string'
    ? chat2
    : isNewsChat2 ? '' : chat2.$jid
  let time1: number
  if (isNewsChat1) {
    time1 = chat1.news[chat1.news.length - 1]?.timestamp || 0
  } else {
    time1 = messages[jid1]?.timestamp || 0
  }
  let time2: number
  if (isNewsChat2) {
    time2 = chat2.news[chat2.news.length - 1]?.timestamp || 0
  } else {
    time2 = messages[jid2]?.timestamp || 0
  }
  return time2 - time1
}

export const isTeamChannel = (chat: Partial<Chat> | null | undefined) => {
  if (chat?.type !== 'groupchat') return false
  const groups = new Set(chat.groups || [])
  return groups.has(GroupCategoriesMap.consultant)
    || groups.has(GroupCategoriesMap.customer)
    || groups.has(GroupCategoriesMap.sponsor)
}

export const isMyTeam = (groups: GroupCategories[] | Set<GroupCategories>) => {
  const groupsSet = groups instanceof Set ? groups : new Set(groups)
  return groupsSet.has(GroupCategoriesMap.sponsor)
    || groupsSet.has(GroupCategoriesMap.customer)
    || groupsSet.has(GroupCategoriesMap.consultant)
}

export const isChannelConsultant = (chat: Chat | null | undefined) => {
  if (chat?.type !== 'groupchat') return false
  const groups = new Set(chat.groups || [])
  return groups.has(GroupCategoriesMap.consultant)
}

export const isChannelCustomer = (chat: Chat | null | undefined) => {
  if (chat?.type !== 'groupchat') return false
  const groups = new Set(chat.groups || [])
  return groups.has(GroupCategoriesMap.customer)
}

export const getNameOrNick = (chat: Chat) => {
  if (chat.type === 'groupchat') {
    return chat.name
  }
  return chat.nick ? chat.nick : chat.name
}

export const formatChatJid = (jid: string) => {
  const index = jid.lastIndexOf('/')
  if (index >= 0) {
    return jid.slice(0, index)
  } else {
    return jid
  }
}
